import { useCallback, useEffect } from 'react';

import { ModelType } from '@sb/robot';

import { useFeatureFlag, getFeatureFlagFromCache } from './useFeatureFlag';
import useRobotState from './useRobotState';

let cachedModelType: ModelType | null = null;

/**
 * The current robot model type, or null while the routine runner has not
 * reported one yet.
 *
 * Use this over useModelType when RO1 is the wrong answer to give under
 * uncertainty, for example before telling the user something that only holds
 * on one model.
 */
export function useModelTypeOrNull(): ModelType | null {
  const useDynamicHardwareConfig = !useFeatureFlag(
    'disableDynamicHardwareConfig',
  );

  const selectModelType = useCallback(
    (state: any) => state?.kinematicState?.modelType ?? null,
    [],
  );

  const stateModelType = useRobotState({}, selectModelType, Object.is);

  useEffect(() => {
    if (useDynamicHardwareConfig) {
      cachedModelType = stateModelType;
    }
  }, [useDynamicHardwareConfig, stateModelType]);

  // With dynamic hardware config off, RO1 is the configured model rather than
  // a stand-in for an unknown one.
  return useDynamicHardwareConfig ? stateModelType : ModelType.enum.RO1;
}

/**
 * Hook to get the current robot model type from the routine runner state.
 */
export function useModelType(): ModelType {
  return useModelTypeOrNull() ?? ModelType.enum.RO1;
}

/**
 * Function to get the current robot model type.
 * Can be used outside of React components.
 *
 * @returns The current robot model type (Thor or RO1)
 */
export function getModelType(): ModelType {
  const useDynamicHardwareConfig = !getFeatureFlagFromCache(
    'disableDynamicHardwareConfig',
  );

  if (useDynamicHardwareConfig && cachedModelType) {
    return cachedModelType;
  }

  return ModelType.enum.RO1;
}
